import { cn } from '@goaltree/ui/lib/utils'
import { Check, Heart, Loader2, Sparkles, Star } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CATEGORY_COLORS, categoryTextColor, useCategoryFill } from '@/lib/category-color'
import { categoryIcon } from '@/lib/category-icon'

export type GoalPace = 'onPace' | 'behind' | 'quiet'

export type GoalSummary = {
  milestone: { index: number; count: number; title: string } | null
  nextTask: { _id: string; title: string } | null
  pace: GoalPace | null
  quietDays: number
  dueDate: number | null
}

export const PACE_CLASS: Record<GoalPace, string> = {
  onPace: 'bg-lime-600/12 text-lime-700 dark:bg-lime-400/12 dark:text-lime-400',
  behind: 'bg-amber-500/12 text-amber-700 dark:text-amber-400',
  quiet: 'bg-muted text-muted-foreground',
}

export function usePaceLabel() {
  const { t, i18n } = useTranslation()
  return (summary: GoalSummary | undefined): string | null => {
    switch (summary?.pace) {
      case 'onPace':
        return t('goals.paceOnPace')
      case 'quiet':
        return t('goalsBoard.quietShort', { count: summary.quietDays })
      case 'behind':
        return summary.dueDate
          ? t('goalsBoard.behindDue', {
              date: new Date(summary.dueDate).toLocaleDateString(i18n.language, { month: 'short' }),
            })
          : t('goals.paceBehind')
      default:
        return null
    }
  }
}

function NextTaskCheck({ onComplete, className }: { onComplete?: () => void; className?: string }) {
  const { t } = useTranslation()
  const [checked, setChecked] = useState(false)
  return (
    <button
      type="button"
      aria-label={t('inbox.markDone')}
      title={t('inbox.markDone')}
      disabled={checked}
      onClick={(e) => {
        e.stopPropagation()
        setChecked(true)
        // Undone before the summary moved on: the same task is back, so un-tick.
        setTimeout(() => setChecked(false), 5000)
        onComplete?.()
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      className={cn(
        'mt-px flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-full border-2 transition-colors',
        checked ? 'border-primary bg-primary text-primary-foreground' : className,
      )}
    >
      {checked && <Check className="h-2.5 w-2.5" strokeWidth={3.5} />}
    </button>
  )
}

interface GoalCardProps {
  title: string
  category: string
  progress: number
  taskCount: number
  remainingCount: number
  likeCount?: number
  coverUrl?: string | null
  videoUrl?: string | null
  isStarred?: boolean
  summary?: GoalSummary
  // Completes the summary's next task straight from the list.
  onCompleteNext?: () => void
  onAddVisual?: () => void
  addingVisual?: boolean
  focused?: boolean
  onTitleClick?: (e: React.MouseEvent) => void
  titleElement?: React.ReactNode
}

// Compact goal row: cover thumb (or a category icon on a soft tint), title, pace,
// current milestone with progress, and the next task with a tick.
export default function GoalCard({
  title,
  category,
  taskCount,
  remainingCount,
  likeCount = 0,
  coverUrl,
  videoUrl,
  isStarred = false,
  summary,
  onCompleteNext,
  onAddVisual,
  addingVisual = false,
  focused = false,
  onTitleClick,
  titleElement,
}: GoalCardProps) {
  const { t, i18n } = useTranslation()
  const fill = useCategoryFill()
  const paceLabel = usePaceLabel()
  const isAllDone = taskCount > 0 && remainingCount === 0
  const completedCount = taskCount - remainingCount
  const color = fill(CATEGORY_COLORS[category.toLowerCase()] ?? '#6b7280')
  const textColor = categoryTextColor(color)
  const categoryLabel = t(`goals.cat.${category.toLowerCase()}`, { defaultValue: category })
  const pct = taskCount > 0 ? Math.round((completedCount / taskCount) * 100) : 0
  const pace = summary?.pace ?? null
  const Icon = categoryIcon(category)
  const hasVisual = !!(coverUrl || videoUrl)

  return (
    <div
      className={cn(
        'relative h-full overflow-hidden rounded-xl border bg-card transition-colors',
        focused ? 'border-primary' : 'border-border/60 hover:border-border',
      )}
    >
      <div className="flex gap-3 p-3">
        <div
          className="group/thumb relative h-12 w-12 shrink-0 overflow-hidden rounded-[10px]"
          style={hasVisual ? undefined : { backgroundColor: `${color}24` }}
        >
          {coverUrl ? (
            <img src={coverUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
          ) : videoUrl ? (
            <video
              src={videoUrl}
              muted
              playsInline
              preload="metadata"
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="flex h-full w-full items-center justify-center" style={{ color }}>
              <Icon className="h-5 w-5" strokeWidth={1.75} />
            </span>
          )}
          {!hasVisual && onAddVisual && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onAddVisual()
              }}
              onMouseDown={(e) => e.stopPropagation()}
              disabled={addingVisual}
              aria-label={t('goalsBoard.addVisual')}
              title={t('goalsBoard.addVisual')}
              className={cn(
                'absolute inset-0 hidden cursor-pointer flex-col items-center justify-center gap-0.5 bg-background/85 text-[9px] font-bold leading-tight text-primary transition-opacity [@media(hover:hover)]:flex',
                addingVisual ? 'opacity-100' : 'opacity-0 group-hover/thumb:opacity-100',
              )}
            >
              {addingVisual ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Sparkles className="h-3.5 w-3.5" />
                  {t('goalsBoard.addVisualShort')}
                </>
              )}
            </button>
          )}
          {isStarred && (
            <Star className="absolute right-0.5 top-0.5 h-3 w-3 fill-yellow-400 text-yellow-400 drop-shadow" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              {titleElement ?? (
                <h3
                  className={cn(
                    'line-clamp-2 text-[15px] font-semibold leading-snug',
                    isAllDone ? 'text-muted-foreground/60 line-through' : 'text-foreground',
                  )}
                >
                  {onTitleClick ? (
                    <span
                      onClick={onTitleClick}
                      className="cursor-text decoration-dashed underline-offset-2 hover:underline"
                    >
                      {title}
                    </span>
                  ) : (
                    title
                  )}
                </h3>
              )}
            </div>
            {pace && (
              <span
                className={cn(
                  'mt-0.5 shrink-0 whitespace-nowrap rounded-full px-2 py-px text-[10.5px] font-bold',
                  PACE_CLASS[pace],
                )}
              >
                {paceLabel(summary)}
              </span>
            )}
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 font-mono text-[11px] text-muted-foreground">
            <span
              className="flex shrink-0 items-center gap-1.5 font-semibold"
              style={{ color: textColor }}
            >
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
              {categoryLabel}
            </span>
            {summary?.dueDate && pace !== 'behind' && (
              <span>
                {t('goals.byDate', {
                  date: new Date(summary.dueDate).toLocaleDateString(i18n.language, {
                    month: 'short',
                    year: 'numeric',
                  }),
                })}
              </span>
            )}
            {likeCount > 0 && (
              <span className="flex items-center gap-0.5">
                <Heart className="h-2.5 w-2.5 shrink-0" />
                <span className="leading-none">{likeCount}</span>
              </span>
            )}
          </div>

          {taskCount > 0 && (
            <div className="mt-1.5 flex items-center gap-2.5 text-xs text-muted-foreground">
              {summary?.milestone ? (
                <p className="min-w-0 truncate">
                  {t('goalsBoard.milestoneShort', {
                    n: summary.milestone.index + 1,
                    total: summary.milestone.count,
                  })}{' '}
                  · <span className="font-semibold text-foreground">{summary.milestone.title}</span>
                </p>
              ) : (
                <span className="shrink-0 font-mono text-[11px] tabular-nums">
                  <span className="font-semibold text-foreground">{completedCount}</span>/
                  {taskCount}
                </span>
              )}
              <div className="relative h-[3px] min-w-10 flex-1 overflow-hidden rounded-full bg-muted sm:max-w-28">
                <div
                  className="absolute left-0 top-0 h-full transition-[width] duration-500"
                  style={{ width: `${pct}%`, backgroundColor: color }}
                />
              </div>
              <span className="shrink-0 font-mono text-[11px] tabular-nums">{pct}%</span>
            </div>
          )}

          {summary?.nextTask && !isAllDone && (
            <div className="mt-2 flex items-start gap-2">
              <NextTaskCheck
                key={summary.nextTask._id}
                onComplete={onCompleteNext}
                className="border-muted-foreground/40 hover:border-primary hover:bg-primary/15"
              />
              <span className="line-clamp-2 min-w-0 text-[13px] leading-snug text-foreground">
                {summary.nextTask.title}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Starred goal as a large cover card: category + pace, title, progress and the
// next task, tickable in place.
export function FocusCard({
  title,
  category,
  taskCount,
  remainingCount,
  coverUrl,
  videoUrl,
  summary,
  onCompleteNext,
  focused = false,
}: Pick<
  GoalCardProps,
  | 'title'
  | 'category'
  | 'taskCount'
  | 'remainingCount'
  | 'coverUrl'
  | 'videoUrl'
  | 'summary'
  | 'onCompleteNext'
  | 'focused'
>) {
  const { t } = useTranslation()
  const fill = useCategoryFill()
  const paceLabel = usePaceLabel()
  const color = fill(CATEGORY_COLORS[category.toLowerCase()] ?? '#6b7280')
  const pct = taskCount > 0 ? Math.round(((taskCount - remainingCount) / taskCount) * 100) : 0
  const pace = paceLabel(summary)
  return (
    <div
      className={cn(
        'relative flex h-full min-h-44 flex-col justify-end overflow-hidden rounded-2xl border p-3.5 text-white',
        focused ? 'border-primary' : 'border-border/60',
      )}
    >
      {coverUrl ? (
        <img
          src={coverUrl}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : videoUrl ? (
        <video
          src={videoUrl}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <div
          className="absolute inset-0"
          style={{ background: `linear-gradient(140deg, #141214, ${color}b3 70%, ${color})` }}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent from-15% to-black/85" />
      <Star className="absolute right-2.5 top-2.5 h-3.5 w-3.5 fill-yellow-400 text-yellow-400 drop-shadow" />
      <div className="relative">
        <p className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-[0.12em] text-white/75">
          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
          {t(`goals.cat.${category.toLowerCase()}`, { defaultValue: category })}
          {pace && <span className="normal-case tracking-normal">· {pace}</span>}
        </p>
        <h3 className="mt-1 line-clamp-2 text-[15px] font-bold leading-tight">{title}</h3>
        {taskCount > 0 && (
          <div className="mt-2 h-[3px] overflow-hidden rounded-full bg-white/20">
            <div
              className="h-full bg-white transition-[width] duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
        {summary?.nextTask && (
          <div className="mt-2 flex items-start gap-2 text-xs text-white/85">
            <NextTaskCheck
              key={summary.nextTask._id}
              onComplete={onCompleteNext}
              className="border-white/70 hover:bg-white/20"
            />
            <span className="line-clamp-2 leading-snug">{summary.nextTask.title}</span>
          </div>
        )}
      </div>
    </div>
  )
}
